import { kbAxiosInstance } from './KBAxiosInstance';
import {
  IDocument,
  IDocumentListResponse,
  IDocumentFilters,
  IDocumentStats,
  IQueryRequest,
  IQueryResponse,
  IRetrieveRequest,
  IRetrieveResponse,
  IUploadResponse,
  IAppConfig,
  IFullSyncAsyncAcceptedResponse,
  IFullPipelineSyncStatus,
} from '../Types/Docs';

// Documents API
export const getDocuments = async (
  filters: IDocumentFilters = {}
): Promise<IDocumentListResponse> => {
  const params = new URLSearchParams();
  
  if (filters.search) {
    params.append('search', filters.search);
  }
  if (filters.status) {
    params.append('status', filters.status);
  }
  if (filters.page) {
    params.append('page', filters.page.toString());
  }
  if (filters.limit) {
    params.append('limit', filters.limit.toString());
  }

  const response = await kbAxiosInstance.get<{
    documents: Array<{
      _id: string;
      ragieId: string;
      name: string;
      displayName?: string;
      status: 'processing' | 'ready' | 'failed';
      source: 'upload' | 'onedrive' | 'onedrive-shared' | 'onedrive-auto-sync';
      metadata: Record<string, unknown>;
      createdAt: string;
      updatedAt: string;
    }>;
    pagination: {
      page: number;
      limit: number;
      total: number;
      totalPages: number;
    };
  }>(`/documents?${params.toString()}`);

  // Transform API response to match expected interface
  return {
    data: response.data.documents.map((doc) => ({
      id: doc._id,
      ragieId: doc.ragieId,
      name: doc.name,
      displayName: doc.displayName,
      status: doc.status,
      source: doc.source,
      metadata: doc.metadata,
      createdAt: doc.createdAt,
      updatedAt: doc.updatedAt,
    })),
    total: response.data.pagination.total,
    page: response.data.pagination.page,
    limit: response.data.pagination.limit,
  };
};

export const getDocument = async (id: string): Promise<IDocument> => {
  const response = await kbAxiosInstance.get<{
    _id: string;
    ragieId: string;
    name: string;
    displayName?: string;
    status: 'processing' | 'ready' | 'failed';
    source: 'upload' | 'onedrive' | 'onedrive-shared' | 'onedrive-auto-sync';
    metadata: Record<string, unknown>;
    createdAt: string;
    updatedAt: string;
  }>(`/documents/${id}`);

  // Transform API response to match expected interface
  return {
    id: response.data._id,
    ragieId: response.data.ragieId,
    name: response.data.name,
    displayName: response.data.displayName,
    status: response.data.status,
    source: response.data.source,
    metadata: response.data.metadata,
    createdAt: response.data.createdAt,
    updatedAt: response.data.updatedAt,
  };
};

export const uploadDocument = async (
  file: File,
  onProgress?: (progress: number) => void,
  categories?: string | string[]
): Promise<IUploadResponse> => {
  const formData = new FormData();
  // API expects 'files' (plural) as the form field name
  formData.append('files', file);

  // Build query parameters for categories
  const params = new URLSearchParams();
  if (categories) {
    const categoriesArray = Array.isArray(categories) ? categories : [categories];
    categoriesArray.forEach((cat) => {
      if (cat.trim()) {
        params.append('categories', cat.trim());
      }
    });
  }

  const url = `/documents/upload${params.toString() ? `?${params.toString()}` : ''}`;

  const response = await kbAxiosInstance.post<IUploadResponse>(
    url,
    formData,
    {
      headers: {
        'Accept': 'application/json',
      },
      onUploadProgress: (progressEvent) => {
        if (progressEvent.total && onProgress) {
          const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          onProgress(progress);
        }
      },
    }
  );
  return response.data;
};

export const deleteDocument = async (id: string): Promise<void> => {
  await kbAxiosInstance.delete(`/documents/${id}`);
};

export const downloadDocument = async (id: string): Promise<Blob> => {
  try {
    const response = await kbAxiosInstance.get(`/documents/${id}/download`, {
      responseType: 'blob',
    });
    
    // Check if response status indicates success
    if (response.status < 200 || response.status >= 300) {
      // Try to parse error message from blob if it's JSON
      if (response.data instanceof Blob && response.data.type?.includes('json')) {
        const text = await response.data.text();
        try {
          const errorData = JSON.parse(text);
          throw new Error(errorData.message || `Download failed with status ${response.status}`);
        } catch {
          throw new Error(`Download failed with status ${response.status}`);
        }
      }
      throw new Error(`Download failed with status ${response.status}`);
    }
    
    // Check if the blob is actually an error response (JSON error wrapped in blob)
    if (response.data instanceof Blob) {
      const contentType = response.data.type || response.headers['content-type'] || '';
      if (contentType.includes('application/json')) {
        const text = await response.data.text();
        try {
          const errorData = JSON.parse(text);
          throw new Error(errorData.message || 'Failed to download document');
        } catch {
          throw new Error('Server returned an error response');
        }
      }
    }
    
    return response.data;
  } catch (error: any) {
    // Handle axios errors
    if (error.response) {
      const { status, data } = error.response;
      
      // If error response is a blob containing JSON, parse it
      if (data instanceof Blob) {
        const contentType = data.type || error.response.headers['content-type'] || '';
        if (contentType.includes('application/json') || data.type === 'application/json') {
          try {
            const text = await data.text();
            const errorData = JSON.parse(text);
            throw new Error(errorData.message || `Download failed: ${status}`);
          } catch (parseError) {
            throw new Error(`Download failed with status ${status}`);
          }
        }
      }
      
      // If error response is already JSON
      if (data && typeof data === 'object' && data.message) {
        throw new Error(data.message);
      }
      
      throw new Error(`Download failed with status ${status}`);
    }
    
    // Re-throw if it's already an Error with message
    if (error instanceof Error) {
      throw error;
    }
    
    throw new Error(error?.message || 'Failed to download document');
  }
};

export const updateDocument = async (
  id: string,
  data: Partial<IDocument>
): Promise<IDocument> => {
  const response = await kbAxiosInstance.patch<IDocument>(`/documents/${id}`, data);
  return response.data;
};

// Query API
export const queryDocuments = async (
  request: IQueryRequest
): Promise<IQueryResponse> => {
  // Transform request to match API format: 'query' -> 'question'
  const apiRequest = {
    question: request.query,
    topK: request.topK,
  };
  const response = await kbAxiosInstance.post<IQueryResponse>('/query', apiRequest);
  return response.data;
};

export const retrieveChunks = async (
  request: IRetrieveRequest
): Promise<IRetrieveResponse> => {
  // Transform request to match API format: 'query' -> 'question'
  const apiRequest = {
    question: request.query,
    topK: request.topK,
  };
  const response = await kbAxiosInstance.post<IRetrieveResponse>(
    '/query/retrieve',
    apiRequest
  );
  return response.data;
};

// Summary API
export const getDocumentSummary = async (): Promise<IDocumentStats> => {
  const response = await kbAxiosInstance.get<IDocumentStats>('/documents/summary');
  return response.data;
};

// Config API
export const getConfig = async (): Promise<IAppConfig> => {
  const response = await kbAxiosInstance.get<IAppConfig>('/config');
  return response.data;
};

// Sync API
export interface ISyncTriggerRequest {
  paths?: string[];
  forceResync?: boolean;
  handleDeletions?: boolean;
}

export interface ISyncTriggerResponse {
  success: boolean;
  message: string;
  syncId?: string;
}

export const triggerSync = async (
  request: ISyncTriggerRequest
): Promise<ISyncTriggerResponse> => {
  const response = await kbAxiosInstance.post<ISyncTriggerResponse>(
    '/sync/trigger',
    request
  );
  return response.data;
};

/** GET /sync/status — use when polling until running becomes false. */
export const getFullPipelineSyncStatus = async (): Promise<IFullPipelineSyncStatus> => {
  const response = await kbAxiosInstance.get<IFullPipelineSyncStatus>('/sync/status');
  return response.data;
};

/**
 * POST /sync/async — 202 accepts; then poll GET /sync/status until running is false.
 * On failure of the job, status.lastError is set; on success use status.lastSync for details.
 */
export const triggerFullSyncAsync = async (): Promise<IFullSyncAsyncAcceptedResponse> => {
  const response = await kbAxiosInstance.post<IFullSyncAsyncAcceptedResponse>(
    '/sync/async',
    {},
    {
      validateStatus: (status) =>
        status === 202 ||
        status === 409 ||
        (status >= 200 && status < 300),
    }
  );

  if (response.status === 202) {
    return response.data;
  }

  if (response.status === 409) {
    const err = new Error('sync_already_running') as Error & { status?: number; responseBody?: unknown };
    err.status = 409;
    err.responseBody = response.data;
    throw err;
  }

  throw new Error(response.data && typeof response.data === 'object' && 'detail' in response.data
    ? String((response.data as { detail?: unknown }).detail)
    : `Unexpected sync start status: ${response.status}`);
};
